import { useEffect } from "react";
import { Box, Button, Group, Select, TextInput } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import { DateInput } from "@mantine/dates";
import {
  clearMultipleClientReports,
  setFiltersMultipleClientReport,
  setPageMultipleClientReport,
  setSizeMultipleClientReport,
} from "../../../../redux/features/reports/multipleClientReportSlice";
import useMultipleClientReportFilters from "./useMultipleClientReportFilters.logic";
import ExportMultiReports from "../../exportButtons/ExportMultiReports";

const MultipleClientReportFilters = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isLoading, setEnabled } = useMultipleClientReportFilters();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm({
    defaultValues: {
      periodFrom: "",
      periodTo: "",
      clientStatus: "",
      changedLevelRisk: "",
      typeOfRule: "",
      ruleName: "",
      caseStatus: "",
      resolutionStatus: "",
      riskLevel: "",
    },
  });

  useEffect(() => {
    return () => {
      onClear();
      setEnabled(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onSubmit(draftFilters) {
    dispatch(setFiltersMultipleClientReport(draftFilters));
    dispatch(setPageMultipleClientReport(0));
    dispatch(setSizeMultipleClientReport(20));
    setEnabled(true);
  }

  function onClear() {
    reset();
    dispatch(clearMultipleClientReports());
  }

  return (
    <Box bg="gray.1" p="md">
      <Group justify="space-between">
        <Group>
          <ExportMultiReports />
        </Group>
        <Group>
          <Button
            disabled={!isDirty}
            variant="outline"
            color="black"
            onClick={onClear}
          >
            Clear
          </Button>
          <Button
            disabled={isLoading || !isDirty}
            onClick={handleSubmit(onSubmit)}
          >
            Generate report
          </Button>
        </Group>
      </Group>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Group mt="md">
          <Controller
            name="periodFrom"
            control={control}
            render={({ field }) => (
              <DateInput
                {...field}
                w={270}
                label={t("reports.fromDate")}
                placeholder="yyyy-MM-dd"
                valueFormat="YYYY-MM-DD"
                clearable
              />
            )}
          />
          <Controller
            name="periodTo"
            control={control}
            render={({ field }) => (
              <DateInput
                {...field}
                w={270}
                label={t("reports.toDate")}
                placeholder="yyyy-MM-dd"
                valueFormat="YYYY-MM-DD"
                clearable
              />
            )}
          />
          <Controller
            name="clientStatus"
            control={control}
            render={({ field }) => (
              <Select
                key={`clientStatus-${field.value || "empty"}`}
                {...field}
                w={270}
                label={t("reports.clientStatus")}
                clearable
                data={[
                  { value: "active", label: "Active" },
                  { value: "inactive", label: "Inactive" },
                ]}
              />
            )}
          />
          <Controller
            name="changedLevelRisk"
            control={control}
            render={({ field }) => (
              <Select
                key={`changedLevelRisk-${field.value || "empty"}`}
                {...field}
                w={270}
                label={t("reports.changedLevelRisk")}
                clearable
                data={[
                  { value: "yes", label: "Yes" },
                  { value: "no", label: "No" },
                ]}
              />
            )}
          />
          <Controller
            name="typeOfRule"
            control={control}
            render={({ field }) => (
              <Select
                key={`typeOfRule-${field.value || "empty"}`}
                {...field}
                w={270}
                label={t("reports.typeOfRule")}
                clearable
                data={[
                  { value: "RTM", label: "RTM" },
                  { value: "PTM", label: "PTM" },
                ]}
              />
            )}
          />
          <Controller
            name="ruleName"
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                w={270}
                label={t("reports.table.ruleName")}
              />
            )}
          />
          <Controller
            name="caseStatus"
            control={control}
            render={({ field }) => (
              <Select
                key={`caseStatus-${field.value || "empty"}`}
                {...field}
                w={270}
                label={t("reports.table.caseStatus")}
                clearable
                data={[
                  { value: "PENDING", label: "Pending" },
                  { value: "IN_PROGRESS", label: "In progress" },
                  { value: "COMPLETED", label: "Completed" },
                  { value: "ESCALATED", label: "Escalated" },
                ]}
              />
            )}
          />
          <Controller
            name="resolutionStatus"
            control={control}
            render={({ field }) => (
              <Select
                key={`resolutionStatus-${field.value || "empty"}`}
                {...field}
                w={270}
                label={t("reports.resolutionStatus")}
                clearable
                data={[
                  { value: "DOCUMENT_ENQUIRY", label: "Document Enquiry" },
                  { value: "FOR_INVESTIGATION", label: "For Investigation" },
                  { value: "FALSE_POSITIVE", label: "False Positive" },
                  {
                    value: "TERMINATION_OF_CONTRACT",
                    label: "Termination of the contract",
                  },
                  { value: "STOP_OPERATION", label: "Stop the operation" },
                  { value: "FOR_REPORTING", label: "For Reporting" },
                  { value: "FOR_MONITORING", label: "For Monitoring" },
                ]}
              />
            )}
          />
          <Controller
            name="riskLevel"
            control={control}
            render={({ field }) => (
              <Select
                key={`riskLevel-${field.value || "empty"}`}
                {...field}
                w={270}
                label={t("reports.riskLevel")}
                clearable
                data={[
                  { value: "low", label: "Low" },
                  { value: "medium", label: "Medium" },
                  { value: "high", label: "High" },
                  { value: "all", label: "All" },
                ]}
              />
            )}
          />
        </Group>
      </form>
    </Box>
  );
};

export default MultipleClientReportFilters;
